/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { ICoreContext } from '@msdyn365-commerce/core';
import { Address, CartLine } from '@msdyn365-commerce/retail-proxy';
import AddressButtonComponent from './address-button';
import { IShipMultipleAddressesTableHeadingProps, ShipMultipleAddressesTableHeading } from './address-ship-multiple-heading';
import { IAddressResource } from '../address-module.data';
import {
    IShipMultipleAddressesLineProps,
    IShipMultipleAddressesLineViewProps,
    ShipMultipleAddressesLineView
} from './address-ship-multiple-lines';
import { IShipMultipleAddressesLineViewModel } from '../../modules/krannich-c-checkout-shipping-address/krannich-c-checkout-shipping-address';
import { ICartLineWithProduct, ICheckoutShippingCartLineInfo } from '../../modules/krannich-c-checkout-shipping-address/components';

export interface IAddressShipMultipleInputProps {
    context: ICoreContext;
    hasExternalSubmitGroup?: boolean;
    moduleId: string;
    moduleTypeName: string;
    currencyCode: string | undefined;
    resources: IAddressResource;
    shippingLines: ICartLineWithProduct[];
    shippingLineImages: ICheckoutShippingCartLineInfo[];
    selectedShippingLines: IShipMultipleAddressesLineViewModel[];
    customerAddresses: Address[];
    onSelect(line: IShipMultipleAddressesLineViewModel, isChecked: boolean): void;
    onSelectAll(isChecked: boolean): void;
    onClear(): void;
    onShippingAddressChange(shippingLine: CartLine, event: React.ChangeEvent<HTMLSelectElement>): void;
    onBulkShippingAddressChange(event: React.ChangeEvent<HTMLSelectElement>): void;
    onShipSingleAddress(): void;
    onSave(): void;
    onCancel(): void;
    isMobile: boolean;
    telemetryContent?: ITelemetryContent;
}

export interface IShipMultipleAddressesViewProps {
    ShipMultipleAddress?: INodeProps;
    shipSingleAddressButton?: React.ReactNode;
    clearSelectionButton?: React.ReactNode;
    addressDropdown?: React.ReactNode;
    ProductsTable?: INodeProps;
    ProductsTableHeading?: React.ReactNode;
    ProductsTableRow?: INodeProps;
    shipMultipleAddressLines?: IShipMultipleAddressesLineViewProps[];
    isShowSaveButton?: boolean;
    saveButton?: React.ReactNode;
    isShowCancelButton?: boolean;
    cancelButton?: React.ReactNode;
    isMobile?: boolean;
}

export const AddressShipMultiple = (props: IAddressShipMultipleInputProps): IShipMultipleAddressesViewProps => {
    const {
        context,
        currencyCode,
        hasExternalSubmitGroup,
        moduleId,
        moduleTypeName,
        shippingLines,
        shippingLineImages,
        selectedShippingLines,
        customerAddresses,
        isMobile,
        onSelect,
        onSelectAll,
        onClear,
        onShippingAddressChange,
        onBulkShippingAddressChange,
        onShipSingleAddress,
        onSave,
        onCancel,
        resources
    } = props;

    const { unavailableProductErrorMessage, outOfStockProductErrorMessage } = props.resources;

    const shipMultipleAddressLines: IShipMultipleAddressesLineViewModel[] = [];

    for (const line of shippingLines) {
        shipMultipleAddressLines.push({
            isChecked: false,
            cartLine: line.cartLine
        });
    }

    function _renderProductListHeader(): React.ReactNode {
        const headers: IShipMultipleAddressesTableHeadingProps = {
            selectAllRowsText: resources.addressSelectAllRowsText,
            productNumber: resources.addressProductNumberText!,
            productName: resources.addressProductText!,
            productUnitPrice: resources.addressProductUnitPriceText!,
            productQuantity: resources.addressProductQuantityText!,
            productAddress: resources.addressProductAddressText,
            clickHandler: (isChecked: boolean) => {
                onSelectAll(isChecked);
            },
            isAllSelected: selectedShippingLines && selectedShippingLines.every(line => line.isChecked),
            isMobile: isMobile,
            clearSelectionButton: (
                <AddressButtonComponent
                    {...{
                        className: 'msc-address-select__clear-selection-button',
                        text: resources.clearSelectionButtonText!,
                        ariaLabel: resources.clearSelectionButtonAriaLabel!,
                        onClick: onClear,
                        telemetryContent: props.telemetryContent,
                        disabled: selectedShippingLines.every(line => !line.isChecked)
                    }}
                />
            )
        };

        return <ShipMultipleAddressesTableHeading {...headers} />;
    }

    function _renderShipMultipleAddressLines(): IShipMultipleAddressesLineViewProps[] {
        return shippingLines.map((line, index) => {
            const currentLineProps: IShipMultipleAddressesLineProps = {
                shipMultipleAddressesLine: {
                    isChecked: (selectedShippingLines && selectedShippingLines[index] && selectedShippingLines[index].isChecked) || false,
                    cartLine: line.cartLine
                },
                shippingLineImage: shippingLineImages[index],
                product: line.product ? line.product : undefined,
                price: line.product?.Price,
                context,
                quantity: line.cartLine?.Quantity!,
                moduleId: moduleId,
                moduleTypeName: moduleTypeName,
                currencyCode: currencyCode,
                customerAddresses: customerAddresses,
                isMobile: isMobile,
                onSelect: onSelect,
                onShippingAddressChange: onShippingAddressChange,
                resources: {
                    unavailableProductErrorMessage,
                    outOfStockProductErrorMessage
                }
            };

            return ShipMultipleAddressesLineView({ ...currentLineProps })!;
        });
    }

    return {
        ShipMultipleAddress: { className: 'msc-address-select' },
        shipSingleAddressButton: (
            <AddressButtonComponent
                {...{
                    className: 'msc-address-select__button-ship-single-address',
                    text: resources.addressShipSingleText!,
                    ariaLabel: resources.addressShipSingleAriaLabel!,
                    onClick: onShipSingleAddress,
                    telemetryContent: props.telemetryContent
                }}
            />
        ),
        clearSelectionButton: (
            <AddressButtonComponent
                {...{
                    className: 'msc-address-select__clear-selection-button',
                    text: resources.clearSelectionButtonText!,
                    ariaLabel: resources.clearSelectionButtonAriaLabel!,
                    onClick: onClear,
                    telemetryContent: props.telemetryContent,
                    disabled: selectedShippingLines.every(line => !line.isChecked)
                }}
            />
        ),
        addressDropdown: (
            <select
                className={'msc-address-select__address-dropdown'}
                title={resources.chooseAddressForSelectedItemsText}
                aria-label={resources.chooseAddressForSelectedItemsAriaLabel}
                onChange={e => onBulkShippingAddressChange(e)}
                defaultValue={''}
                disabled={selectedShippingLines.every(line => !line.isChecked)}
            >
                <option value='' aria-label={resources.noAddressSelectedAriaLabel} disabled hidden>
                    {resources.chooseAddressForSelectedItemsText}
                </option>
                {customerAddresses?.map((address: Address, index: number) => (
                    <option key={index} value={address.RecordId} aria-label={address.FullAddress}>
                        {address.FullAddress}
                    </option>
                ))}
            </select>
        ),
        ProductsTable: {
            className: `ms-address-select__table`,
            tag: 'table',
            role: 'grid'
        },
        ProductsTableRow: {
            className: `ms-address-select-table-line`,
            tag: 'tr'
        },
        ProductsTableHeading: _renderProductListHeader(),
        shipMultipleAddressLines: _renderShipMultipleAddressLines(),
        isShowSaveButton: !hasExternalSubmitGroup,
        saveButton: (
            <AddressButtonComponent
                {...{
                    className: 'msc-address-select__save-continue-button',
                    text: resources.saveAndContinueBtnLabel!,
                    ariaLabel: resources.addressSaveButtonAriaLabel,
                    onClick: onSave,
                    telemetryContent: props.telemetryContent
                }}
            />
        ),
        isShowCancelButton: !hasExternalSubmitGroup,
        cancelButton: (
            <AddressButtonComponent
                {...{
                    className: 'msc-address-select__cancel-button',
                    text: resources.addressCancelButtonText,
                    ariaLabel: resources.addressCancelButtonAriaLabel,
                    onClick: onCancel,
                    telemetryContent: props.telemetryContent
                }}
            />
        ),
        isMobile: isMobile
    };
};
